import {
  brandPath,
  type NavGroup,
  type NavItem,
  type NavLink,
  type NavMenu,
} from '@/config/navigation';

/** The label on the pill that holds whatever did not fit on the row. */
export const OVERFLOW_MENU_LABEL = 'More';

/**
 * Folds the tail of the primary nav into a single `NavMenu`, so the header's
 * last resort for a row that will not fit is one more pill rather than a
 * missing link.
 *
 * It returns a `NavMenu` and nothing else, which is the whole reason this is a
 * function and not a component: `NavMenuButton` and `NavDropdown` already know
 * how to draw a pill with a panel under it, how to open it on hover only where
 * hover exists, how to walk it with the arrow keys and how to mark it active
 * when the current page is inside it. A bespoke "More" dropdown would be a
 * second implementation of all of that, drifting from the first one menu
 * behaviour at a time.
 *
 * ## How the two shapes fold
 *
 * A folded **link** becomes a plain item, and `NavDropdown` renders those in a
 * full-width list below the divider.
 *
 * A folded **menu** becomes a titled group, which is the one shape that keeps
 * the structure the visitor would have found by opening that menu themselves:
 * its name over its own links. Flattening everything into one long list would
 * put "Reviews" and "Tax Calculator" in the same undifferentiated column, and
 * the heading is the only thing that says which of them answers which
 * question. `groups` also costs nothing extra to render — the Services menu
 * has used it since it was written.
 *
 * A menu's own `groups` are flattened into its links rather than nested: the
 * panel lays groups out as columns, and a column of columns is not a thing it
 * can draw. This only matters in theory — `useHeaderFit` pins the first three
 * items, and the two mega-menus are both inside that pin — but the fold is
 * written to survive a reordering of `primaryNav` that moves one of them.
 */
export function buildOverflowMenu(items: readonly NavItem[]): NavMenu {
  const links: NavLink[] = [];
  const groups: NavGroup[] = [];

  for (const item of items) {
    if (item.kind === 'link') {
      links.push({ label: item.label, to: item.to });
      continue;
    }
    groups.push({
      title: item.label,
      items: [
        ...item.items,
        ...(item.groups ?? []).flatMap((group) => group.items),
        ...(item.brands ?? []).map((brand) => ({ label: brand.name, to: brandPath(brand) })),
      ],
    });
  }

  return { label: OVERFLOW_MENU_LABEL, items: links, ...(groups.length > 0 && { groups }) };
}
